import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Wallet, Plus, Send, ArrowDownLeft, ArrowUpRight, 
  CreditCard, Smartphone, Building, Loader2, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount, currency = 'KES') => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function WalletPage() {
  const { token, user, refreshUser } = useAuth();
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWallet();
  }, []);

  const fetchWallet = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wallet`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalletData(response.data);
    } catch (error) {
      console.error('Wallet fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/wallet/topup`, {
        amount: amount,
        currency: 'KES'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        toast.success('Wallet topped up successfully!');
        setTopupAmount('');
        fetchWallet();
        refreshUser();
      }
    } catch (error) {
      toast.error('Failed to top up wallet');
    } finally {
      setProcessing(false);
    }
  };

  const quickAmounts = [1000, 5000, 10000, 50000];

  return (
    <AppLayout title="Wallet">
      <div className="p-4 lg:p-6 space-y-6">
        {/* Balance Card */}
        <Card className="wallet-gradient text-white overflow-hidden" data-testid="wallet-balance-card">
          <CardContent className="p-6 lg:p-8">
            <div className="flex items-start justify-between mb-8">
              <div>
                <p className="text-white/80 text-sm mb-1">Available Balance</p>
                {loading ? (
                  <Skeleton className="h-12 w-48 bg-white/20" />
                ) : (
                  <p className="text-4xl lg:text-5xl font-bold tabular-nums">
                    {formatCurrency(walletData?.balance || user?.wallet_balance || 0)}
                  </p>
                )}
              </div>
              <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                <Wallet className="h-7 w-7" />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button variant="secondary" className="flex-1 rounded-full h-12" data-testid="add-money-main">
                <Plus className="h-5 w-5 mr-2" />
                Add Money
              </Button>
              <Button variant="secondary" className="flex-1 rounded-full h-12" data-testid="send-money-main">
                <Send className="h-5 w-5 mr-2" />
                Send
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Invest Reminder */}
        <Card className="invest-gradient border-0" data-testid="quick-invest-reminder">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-orange-500 flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-medium">Enable Quick Invest</p>
                <p className="text-xs text-muted-foreground">Auto-invest spare change from purchases</p>
              </div>
            </div>
            <Button size="sm" className="rounded-full bg-gradient-to-r from-green-500 to-orange-500 text-white">
              Enable
            </Button>
          </CardContent>
        </Card>

        {/* Quick Top-up */}
        <Card data-testid="quick-topup-card">
          <CardHeader>
            <CardTitle>Add Money</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map((amount) => (
                <Button
                  key={amount}
                  variant={topupAmount === String(amount) ? 'default' : 'outline'}
                  onClick={() => setTopupAmount(String(amount))}
                  className={`rounded-full ${topupAmount === String(amount) ? 'jambo-gradient text-white' : ''}`}
                  data-testid={`quick-amount-${amount}`}
                >
                  {formatCurrency(amount)}
                </Button>
              ))}
            </div>
            
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Enter amount"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                className="flex-1"
                data-testid="topup-amount-input"
              />
              <Button 
                onClick={handleTopup}
                disabled={processing || !topupAmount}
                className="rounded-full px-8 jambo-gradient text-white"
                data-testid="topup-submit-btn"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Top Up'}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Using mock payment for demo. In production, this would use Flutterwave.
            </p>
          </CardContent>
        </Card>

        {/* Payment Methods */}
        <Card>
          <CardHeader>
            <CardTitle>Payment Methods</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { icon: Smartphone, name: 'M-Pesa', description: 'Mobile money', connected: true },
              { icon: CreditCard, name: 'Debit/Credit Card', description: 'Visa, Mastercard', connected: false },
              { icon: Building, name: 'Bank Transfer', description: 'Direct bank deposit', connected: false },
            ].map((method) => (
              <div 
                key={method.name}
                className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                data-testid={`payment-method-${method.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-green-500 flex items-center justify-center">
                    <method.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium">{method.name}</p>
                    <p className="text-xs text-muted-foreground">{method.description}</p>
                  </div>
                </div>
                <Button 
                  variant={method.connected ? 'outline' : 'default'} 
                  size="sm" 
                  className={`rounded-full ${!method.connected ? 'jambo-gradient text-white' : ''}`}
                >
                  {method.connected ? 'Connected' : 'Add'}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card data-testid="transaction-history-card">
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {Array(5).fill(0).map((_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-1/3 mb-1" />
                      <Skeleton className="h-3 w-1/4" />
                    </div>
                    <Skeleton className="h-5 w-20" />
                  </div>
                ))}
              </div>
            ) : walletData?.transactions?.length ? (
              <div className="divide-y">
                {walletData.transactions.map((tx) => (
                  <div key={tx.id} className="py-4 flex items-center gap-3" data-testid={`transaction-${tx.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      tx.type === 'topup' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      {tx.type === 'topup' ? (
                        <ArrowDownLeft className="h-5 w-5" />
                      ) : (
                        <ArrowUpRight className="h-5 w-5" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium capitalize">{tx.type}</p>
                      <p className="text-xs text-muted-foreground truncate">{tx.description}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${tx.type === 'topup' ? 'text-green-600' : 'text-orange-600'}`}>
                        {tx.type === 'topup' ? '+' : '-'}{formatCurrency(tx.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center text-muted-foreground">
                <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No transactions yet</p>
                <p className="text-sm">Top up your wallet to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
